import {
  TABLE_SORTER_TOOLTIP,
  LIST_CREATE_BUTTON_STYLE,
  LIST_SURFACE_STYLE,
} from '../../../shared/ui/list-styles.js';
import { QuietEmpty } from '../../../shared/ui/quiet-states.js';
// Platform admin plans screen (archetype: DataView, cf. AdminUsersPage).
// Plans are the commercial tier catalogue: price, cost, token/storage limits
// and ordering are managed here instead of being hard-coded (owner decision
// 2026-10-20). Deactivation replaces deletion — active workspaces keep
// referencing the plan code, so rows are never removed, only switched off.
import { useMemo, useState, type ReactElement } from 'react';
import { Badge, Button, Popconfirm, Table, Tooltip, Typography, message } from 'antd';
import type { ColumnType } from 'antd/es/table/interface';
import { EditOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import { useTranslation } from '@govard/i18n-core/react';

import { PageHeader } from '../../../shared/ui/PageHeader.js';
import { ErrorState } from '../../../shared/ui/ErrorState.js';
import { formatAdminCount, formatPlanPrice, formatPlanStorage } from '../lib/format.js';
import { useAdminPlans } from '../hooks/useAdminPlans.js';
import { useAdminPlansMutations } from '../hooks/useAdminPlansMutations.js';
import {
  AdminPlanFormModal,
  type AdminPlanFormSubmitInput,
} from '../components/AdminPlanFormModal.js';
import type { AdminPlanConfig } from '../api/admin.client.js';

const { Text } = Typography;

export function AdminPlansPage(): ReactElement {
  const { t } = useTranslation();
  const plansQuery = useAdminPlans();
  const rows = useMemo(() => plansQuery.data ?? [], [plansQuery.data]);

  const [modalOpen, setModalOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState<AdminPlanConfig | null>(null);
  const { createMutation, updateMutation, deactivateMutation } = useAdminPlansMutations();
  const pending =
    createMutation.isPending || updateMutation.isPending || deactivateMutation.isPending;
  const submitError = createMutation.error ?? updateMutation.error;

  const columns = useMemo<Array<ColumnType<AdminPlanConfig>>>(
    () => [
      {
        title: t('crm.admin.plans.col_plan'),
        key: 'plan',
        render: (_: unknown, plan) => (
          <div className="flex min-w-0 flex-col gap-0.5">
            <Text strong style={{ color: 'var(--ant-color-text)', fontSize: 13 }}>
              {plan.displayName}
            </Text>
            <Text className="font-mono" type="secondary" style={{ fontSize: 11 }} ellipsis>
              {plan.code}
            </Text>
          </div>
        ),
      },
      {
        title: t('crm.admin.plans.col_price'),
        dataIndex: 'priceCents',
        key: 'priceCents',
        width: 110,
        align: 'right',
        render: (value: number) => (
          <Text className="font-mono" style={{ fontSize: 12 }}>
            {formatPlanPrice(value)}
          </Text>
        ),
      },
      {
        title: t('crm.admin.plans.col_cost'),
        dataIndex: 'costCents',
        key: 'costCents',
        width: 110,
        align: 'right',
        render: (value: number) => (
          <Text className="font-mono" type="secondary" style={{ fontSize: 12 }}>
            {formatPlanPrice(value)}
          </Text>
        ),
      },
      {
        title: t('crm.admin.plans.col_tokens'),
        key: 'tokenLimitMonthly',
        width: 130,
        align: 'right',
        render: (_: unknown, plan) => (
          <Tooltip title={t('crm.admin.plans.tokens_tooltip')}>
            <Text className="font-mono" style={{ fontSize: 12 }}>
              {formatAdminCount(Number(plan.tokenLimitMonthly))}
            </Text>
          </Tooltip>
        ),
      },
      {
        title: t('crm.admin.plans.col_storage'),
        key: 'storageLimitBytes',
        width: 110,
        align: 'right',
        render: (_: unknown, plan) => (
          <Text className="font-mono" style={{ fontSize: 12 }}>
            {formatPlanStorage(plan.storageLimitBytes)}
          </Text>
        ),
      },
      {
        title: t('crm.admin.plans.col_period'),
        dataIndex: 'billingPeriod',
        key: 'billingPeriod',
        width: 110,
        render: (value: string) =>
          value === 'monthly' ? (
            <Text style={{ fontSize: 12 }}>{t('crm.admin.plans.period_monthly')}</Text>
          ) : (
            <Text className="font-mono" style={{ fontSize: 12 }}>
              {value}
            </Text>
          ),
      },
      {
        title: t('crm.admin.plans.col_order'),
        dataIndex: 'sortOrder',
        key: 'sortOrder',
        width: 80,
        align: 'right',
        render: (value: number) => (
          <Text className="font-mono" style={{ fontSize: 12 }}>
            {value}
          </Text>
        ),
      },
      {
        title: t('crm.admin.plans.col_status'),
        key: 'status',
        width: 120,
        render: (_: unknown, plan) =>
          plan.isActive ? (
            <Badge
              status="success"
              text={<span style={{ fontSize: 11 }}>{t('crm.admin.plans.status_active')}</span>}
            />
          ) : (
            <Badge
              status="default"
              text={<span style={{ fontSize: 11 }}>{t('crm.admin.plans.status_inactive')}</span>}
            />
          ),
      },
      {
        title: t('crm.admin.plans.col_actions'),
        key: 'actions',
        width: 210,
        render: (_: unknown, plan) => (
          <div className="flex items-center gap-1">
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={() => {
                setEditingPlan(plan);
                setModalOpen(true);
              }}
              aria-label={t('crm.admin.plans.action_edit', { name: plan.displayName })}
              data-testid="admin-plan-edit"
            >
              {t('crm.admin.plans.action_edit_short')}
            </Button>
            {plan.isActive ? (
              <Popconfirm
                title={t('crm.admin.plans.action_deactivate')}
                description={t('crm.admin.plans.confirm_deactivate', { name: plan.displayName })}
                okText={t('crm.admin.plans.action_deactivate')}
                okButtonProps={{ danger: true, loading: pending }}
                cancelText={t('crm.common.cancel')}
                onConfirm={() => {
                  void deactivateMutation
                    .mutateAsync(plan.id)
                    .then(() => {
                      void message.success(t('crm.admin.plans.deactivated'));
                    })
                    .catch(() => {
                      void message.error(t('crm.admin.errors.action_failed'));
                    });
                }}
              >
                <Button size="small" danger loading={pending} data-testid="admin-plan-deactivate">
                  {t('crm.admin.plans.action_deactivate_short')}
                </Button>
              </Popconfirm>
            ) : (
              <Button
                size="small"
                loading={pending}
                data-testid="admin-plan-activate"
                onClick={() => {
                  void updateMutation
                    .mutateAsync({ planId: plan.id, isActive: true })
                    .then(() => {
                      void message.success(t('crm.admin.plans.activated'));
                    })
                    .catch(() => {
                      void message.error(t('crm.admin.errors.action_failed'));
                    });
                }}
              >
                {t('crm.admin.plans.action_activate')}
              </Button>
            )}
          </div>
        ),
      },
    ],
    [t, pending, deactivateMutation, updateMutation],
  );

  function handleModalSubmit(input: AdminPlanFormSubmitInput): void {
    if (editingPlan !== null) {
      void updateMutation
        .mutateAsync({
          planId: editingPlan.id,
          displayName: input.displayName,
          priceCents: input.priceCents,
          costCents: input.costCents,
          tokenLimitMonthly: input.tokenLimitMonthly,
          storageLimitBytes: input.storageLimitBytes,
          billingPeriod: input.billingPeriod,
          sortOrder: input.sortOrder,
        })
        .then(() => {
          setModalOpen(false);
          void message.success(t('crm.admin.plans.saved'));
        })
        .catch(() => {
          // Error is rendered inside the modal; keep it open.
        });
      return;
    }
    void createMutation
      .mutateAsync(input)
      .then(() => {
        setModalOpen(false);
        void message.success(t('crm.admin.plans.created'));
      })
      .catch(() => {
        // Error is rendered inside the modal; keep it open.
      });
  }

  if (plansQuery.isError) {
    return (
      <ErrorState
        title={t('crm.admin.plans.load_error')}
        error={plansQuery.error}
        onRetry={() => void plansQuery.refetch()}
      />
    );
  }

  return (
    <div className="animate-fade-in flex flex-col">
      <PageHeader
        title={t('crm.admin.plans.title')}
        subtitle={t('crm.admin.plans.subtitle', { total: rows.length })}
        actions={
          <>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => void plansQuery.refetch()}
              loading={plansQuery.isFetching}
              aria-label={t('crm.admin.plans.reload')}
              style={LIST_CREATE_BUTTON_STYLE}
            >
              {t('crm.admin.plans.reload')}
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setEditingPlan(null);
                setModalOpen(true);
              }}
              data-testid="admin-plan-create"
            >
              {t('crm.admin.plans.create')}
            </Button>
          </>
        }
      />

      <div className="flex min-h-0 flex-1 flex-col gap-3">
        <div className="min-h-0 flex-1" style={LIST_SURFACE_STYLE}>
          <Table<AdminPlanConfig>
            showSorterTooltip={TABLE_SORTER_TOOLTIP}
            size="small"
            rowKey="id"
            columns={columns}
            dataSource={rows}
            loading={plansQuery.isLoading}
            pagination={false}
            locale={{
              emptyText: <QuietEmpty text={t('crm.admin.plans.empty')} />,
            }}
            data-testid="admin-plans-table"
          />
        </div>
      </div>

      <AdminPlanFormModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        plan={editingPlan}
        pending={createMutation.isPending || updateMutation.isPending}
        submitError={submitError}
        onSubmit={handleModalSubmit}
      />
    </div>
  );
}
